import PropTypes from 'prop-types'
import BasicObject from 'model/basicObject'
import { omit } from 'ramda'
import RiskScore from 'model/person/medicalHistory/riskScore'
import CareGap from 'model/person/medicalHistory/careGap'
import {
  LOW_RISK_BTN_STYLE,
  MEDIUM_RISK_BTN_STYLE,
  HIGH_RISK_BTN_STYLE
} from 'constants/styles'

class MedicalHistory extends BasicObject {
  constructor(init) {
    // console.log('> MedicalHistory constructor -', init)
    super(init)
    this._risk_score = new RiskScore(init?.risk_score || {})
    this._care_gaps = init?.care_gaps
      ? init?.care_gaps.map(care_gap => new CareGap(care_gap))
      : []
    this._diagnoses = init?.diagnoses
    this._visits = init?.visits
    this._timeline = init?.timeline
    this._employee_health_determinants = init?.employee_health_determinants
    this._health_determinants = init?.health_determinants
    this._chronic_conditions = init?.chronic_conditions
    this._enabled_care_gaps_count = init?.enabled_care_gaps_count
    this._allergies = init?.allergies
  }

  static shape = {
    risk_score: PropTypes.instanceOf(RiskScore),
    care_gaps: PropTypes.arrayOf(PropTypes.instanceOf(CareGap)).isRequired,
    diagnoses: PropTypes.array.isRequired,
    visits: PropTypes.array.isRequired,
    timeline: PropTypes.array.isRequired,
    employee_health_determinants: PropTypes.array.isRequired,
    health_determinants: PropTypes.array.isRequired,
    chronic_conditions: PropTypes.array.isRequired,
    enabled_care_gaps_count: PropTypes.number.isRequired,
    allergies: PropTypes.array.isRequired
  }

  get risk_score() {
    return this._risk_score
  }

  get care_gaps() {
    return this._care_gaps
  }

  get diagnoses() {
    return this._diagnoses || []
  }

  get visits() {
    return this._visits || []
  }

  get timeline() {
    return this._timeline || []
  }

  get employee_health_determinants() {
    return this._employee_health_determinants || []
  }

  get health_determinants() {
    return this._health_determinants || []
  }

  get allergies() {
    return this._allergies || []
  }

  get chronic_conditions() {
    return this._chronic_conditions || []
  }

  get enabled_care_gaps_count() {
    return this._enabled_care_gaps_count || 0
  }

  // Calculable

  get isEmpty() {
    const {
      risk_score,
      care_gaps,
      diagnoses,
      visits,
      timeline,
      employee_health_determinants,
      health_determinants,
      chronic_conditions,
      allergies
    } = this

    if (
      !risk_score.id &&
      !care_gaps.length &&
      !diagnoses.length &&
      !visits.length &&
      !timeline.length &&
      !employee_health_determinants.length &&
      !health_determinants.length &&
      !chronic_conditions.length &&
      !allergies
    )
      return true

    return false
  }

  get representRiskScore() {
    const value = this.risk_score.normalizeRiskScore
    if (value === '--') return { ...LOW_RISK_BTN_STYLE, name: 'No Risk Score' }
    if (value < 50)
      return { ...LOW_RISK_BTN_STYLE, name: `Low Risk (${value})` }
    if (value < 75)
      return { ...MEDIUM_RISK_BTN_STYLE, name: `Medium Risk (${value})` }
    return { ...HIGH_RISK_BTN_STYLE, name: `High Risk (${value})` }
  }

  get representCareGaps() {
    const value = this.enabled_care_gaps_count
    if (value < 3) return { ...LOW_RISK_BTN_STYLE, name: `No Care Gaps` }
    if (value < 5)
      return { ...MEDIUM_RISK_BTN_STYLE, name: `${value} Care Gaps` }
    return { ...HIGH_RISK_BTN_STYLE, name: `${value} Care Gaps` }
  }

  get combinedDeterminants() {
    const determinants = this.health_determinants.map(item =>
      omit(['__typename'], item)
    )
    const result = determinants.map(item => {
      const match = this.employee_health_determinants.find(
        i => i.health_determinant_id === item.id
      )

      return {
        ...item,
        enabled: !!match,
        note: match?.note,
        statusId: match?.id
      }
    })

    return result
  }
}

export default MedicalHistory
